import {
  CalendarDays,
  Camera,
  CircleCheck,
  CircleDot,
  Flag,
  GraduationCap,
  MapPin,
  Trophy,
  Users,
} from "lucide-react";

export const camp = {
  registrationUrl: "https://form.jotform.com/262444018606050",
  registrationUrlLabel: "Jotform registration page",
  registrationEnabled: true,
  registrationPausedMessage: "Registration temporarily paused while payment setup is finalized.",
  stripeLookupKey: "alabama_elite_junior_golf_camp_250",
  brandName: "Top Tier Sports Camps",
  brandLine: "Creating elite sports experiences for the next generation.",
  eventName: "Alabama Elite Junior Golf Camp",
  heroKicker: "Train. Compete. Get Better.",
  description:
    "An elite one-day junior golf experience giving young golfers the opportunity to train, compete, and learn alongside high-level collegiate golfers.",
  date: "November 15, 2026",
  dateShort: "NOV 15 / 2026",
  venue: "Oak Mountain State Park Golf Course",
  city: "Birmingham, Alabama",
  ageRange: "Ages 7–18",
  price: "$250",
  spotsRemaining: "UPDATE BEFORE LAUNCH",
  directionsUrl:
    "https://www.google.com/maps/search/?api=1&query=Oak+Mountain+State+Park+Golf+Course+Birmingham+Alabama",
  quickInfo: [
    { label: "Date", value: "November 15, 2026", icon: CalendarDays },
    { label: "Venue", value: "Oak Mountain State Park Golf Course", icon: MapPin },
    { label: "Format", value: "One-day elite golf experience", icon: Flag },
    { label: "Core", value: "Train • Compete • Learn", icon: Trophy },
  ],
  experienceIntro:
    "Campers rotate through focused golf training stations, interact with elite collegiate golfers, participate in competitions, and learn what it takes to improve their game and compete at a higher level.",
  experienceCards: [
    { title: "Driving", text: "Power, accuracy, setup, and consistency.", icon: "circle" },
    { title: "Iron Play", text: "Ball striking, distance control, alignment, and approach shots.", icon: "target" },
    { title: "Short Game", text: "Chipping, pitching, creativity, and scoring around the green.", icon: "dot" },
    { title: "Putting", text: "Green reading, speed control, setup, and competitive putting.", icon: "flag" },
    { title: "Competition", text: "Fun skills challenges and competitions throughout the camp.", icon: "trophy" },
    { title: "College Golf Experience", text: "Learn directly from high-level collegiate golfers about practice, competition, preparation, and what it takes to reach the next level.", icon: "graduation" },
  ],
  chapters: [
    { number: "01", title: "The Experience", tagline: "A day built around real improvement", text: "Focused stations give every camper a clear place to work, test, and refine the parts of the game that matter most." },
    { number: "02", title: "The People", tagline: "Access to the next level", text: "Campers learn directly from accomplished collegiate golfers who understand the path from junior golf to college competition." },
    { number: "03", title: "The Practice", tagline: "Habits that travel", text: "Players hear how high-level golfers prepare, practice, compete, and stay ready when the score matters." },
    { number: "04", title: "The Competition", tagline: "Pressure makes progress", text: "Skills challenges create a fun, competitive environment where golfers can test new skills with confidence." },
    { number: "05", title: "The Next Step", tagline: "Leave with more belief", text: "The goal is simple: help each junior golfer see a clearer, more exciting path for their game." },
  ],
  schedule: [
    ["9:00 AM", "Camper Check-In"],
    ["9:30 AM", "Welcome & Player Introductions"],
    ["9:45 AM", "Training Stations Begin"],
    ["12:30 PM", "Lunch & College Golf Q&A"],
    ["1:15 PM", "Skills Competitions & Challenges"],
    ["2:15 PM", "Awards, Photos & Closing"],
    ["2:45 PM", "Camp Concludes"],
  ],
  coaches: [
    {
      name: "William Jennings",
      college: "University of Alabama",
      classYear: "Junior",
      role: "Collegiate Golfer",
      hometown: "Greenville, S.C.",
      school: "Christ Church HS",
      photo: "https://customer-assets-jai6qajn.emergentagent.net/job_alabama-elite-golf/artifacts/4zghtbqr_image.png",
      bio: "A junior from Greenville, S.C., Jennings earned medalist honors at the 2025 Folds of Honor Collegiate and was named to the Haskins Award Final Fall Watch List.",
      accomplishments: "2025 Folds of Honor Collegiate medalist • 2025 U.S. Amateur qualifier • SEC Academic Honor Roll (2025)",
      profileUrl: "https://rolltide.com/sports/mens-golf/roster/william-jennings/18198",
    },
    {
      name: "Nick Gross",
      college: "University of Alabama",
      classYear: "Junior",
      role: "Collegiate Golfer",
      hometown: "Downingtown, Pa.",
      school: "Downingtown West HS",
      photo: "https://customer-assets-jai6qajn.emergentagent.net/job_alabama-elite-golf/artifacts/fz0al4hc_image.png",
      bio: "A junior from Downingtown, Pa., Gross was part of the 2023 Junior Ryder Cup team and earned First Team Rolex Junior All-American honors.",
      accomplishments: "2023 Junior Ryder Cup team • 2023 First Team Rolex Junior All-American • 2025 U.S. Amateur qualifier",
      profileUrl: "https://rolltide.com/sports/mens-golf/roster/nick-gross/18196",
    },
    {
      name: "Michael Crocker",
      college: "University of Alabama",
      classYear: "Junior",
      role: "Collegiate Golfer",
      hometown: "Mobile, Ala.",
      school: "McGill-Toolen HS",
      photo: "https://customer-assets-jai6qajn.emergentagent.net/job_alabama-elite-golf/artifacts/fz37r6n2_image.png",
      bio: "A junior from Mobile, Ala., Crocker won the 2023 Alabama Boys State Junior Championship and posted a career-best eighth-place finish at the 2025 Turtle Point Invite.",
      accomplishments: "2023 Alabama Boys State Junior champion • SEC Academic Honor Roll (2025) • 2025 Turtle Point Invite eighth place",
      profileUrl: "https://rolltide.com/sports/mens-golf/roster/michael-crocker/18195",
    },
    {
      name: "Jack Mitchell",
      college: "University of Alabama",
      classYear: "Senior",
      role: "Collegiate Golfer",
      hometown: "Athens, Ala.",
      school: "James Clemens HS",
      height: "6-0",
      photo: "https://customer-assets-jai6qajn.emergentagent.net/job_alabama-elite-golf/artifacts/45s8item_image.png",
      bio: "A senior from Athens, Ala., Mitchell finished fourth at the 2025 Folds of Honor Collegiate and brings experience from every fall event on Alabama's schedule.",
      accomplishments: "2025 Folds of Honor Collegiate fourth place • SEC Academic Honor Roll (2024, 2025) • 6-0",
      profileUrl: "https://rolltide.com/sports/mens-golf/roster/jack-mitchell/18200",
    },
    {
      name: "Miles Eubanks",
      college: "University of Alabama",
      classYear: "Junior",
      role: "Collegiate Golfer",
      hometown: "Warrensville, Ga.",
      school: "South Aiken",
      previousSchool: "College of Charleston",
      photo: "https://customer-assets-jai6qajn.emergentagent.net/job_alabama-elite-golf/artifacts/4ohcgnxw_image.png",
      bio: "A junior transfer from the College of Charleston, Eubanks joins Alabama after earning 2026 PING All-East Region and All-CAA First Team honors.",
      accomplishments: "2026 PING All-East Region • 2026 All-CAA First Team • 2026 CAA Golfer of the Week",
      profileUrl: "https://rolltide.com/sports/mens-golf/roster/miles-eubanks/18204",
    },
  ],
  audiences: [
    ["Beginner", "Learning the fundamentals and building confidence."],
    ["Intermediate", "Regular golfers looking to improve consistency and scoring."],
    ["Advanced", "Experienced junior golfers who play competitively."],
    ["Elite", "Tournament golfers looking to sharpen their game and learn from college-level players."],
  ],
  inclusions: [
    "Elite golf instruction",
    "Multiple training stations",
    "Skills competitions",
    "Interaction with collegiate golfers",
    "Lunch",
    "Camp T-shirt",
    "Camp merchandise",
    "Awards/prizes",
    "Photos",
    "Full-day camp experience",
  ],
  parentTrust: [
    ["Parent/guardian registration", "Registration information is completed by a parent or guardian."],
    ["Emergency contact information", "Emergency contact details will be collected during registration."],
    ["Waivers", "Required waiver information will be provided with registration."],
    ["Camp supervision", "Final supervision details will be published as camp programming is completed."],
    ["Weather procedures", "Weather updates and procedures will be shared with registered families."],
    ["Check-in/check-out", "Arrival and departure guidance will be shared before camp day."],
    ["Food/allergy information", "Families can provide food and allergy information during registration."],
    ["What campers should bring", "A final bring list will be sent to registered families."],
  ],
  faqs: [
    ["What skill level does my child need?", "The camp welcomes beginner through elite junior golfers. Choose the level that best fits your child's current experience."],
    ["What ages can attend?", "The current editable age range is 7–18."],
    ["What should my child bring?", "Golf clubs and a positive attitude."],
    ["Does my child need their own golf clubs?", "Yes."],
    ["Is lunch provided?", "Yes. Lunch is currently listed as part of what campers receive."],
    ["What happens if it rains?", "Weather procedures will be shared with registered families as final camp policies are completed."],
    ["Can parents stay and watch?", "Parent viewing guidance will be shared before camp and can be updated here."],
    ["Who will be coaching the camp?", "University of Alabama golfers and experienced golfers."],
    ["What is the refund policy?", "The refund policy will be published with the final registration details."],
    ["Where do I check in?", "Camper check-in begins at Oak Mountain State Park Golf Course at 9:00 AM."],
    ["How do I register?", "Select any Register Now button to open the Jotform registration page in a new tab."],
  ],
  images: {
    hero: "https://customer-assets-jai6qajn.emergentagent.net/job_alabama-elite-golf/artifacts/zv3t5nul_image.png",
    course: "https://images.unsplash.com/photo-1763917379121-91130139aca0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxnb2xmJTIwY291cnNlJTIwZ3JlZW4lMjBmbGFnfGVufDB8fHx8MTc4ODE4ODEwMHww&ixlib=rb-4.1.0&q=85",
    putting: "https://images.unsplash.com/photo-1532508583690-538a1436f423?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxnb2xmZXIlMjBwdXR0aW5nfGVufDB8fHx8MTc4ODE4ODEwN3ww&ixlib=rb-4.1.0&q=85",
    junior: "https://customer-assets-jai6qajn.emergentagent.net/job_alabama-elite-golf/artifacts/v0qgzu4m_image.png",
  },
  socialLinks: [
    ["Instagram", "https://www.instagram.com/toptiersportscamps/"],
    ["Facebook", "#facebook-placeholder"],
  ],
  contactEmail: "CONTACT EMAIL COMING SOON",
};

export const experienceIconMap = {
  circle: CircleDot,
  target: CircleCheck,
  dot: CircleDot,
  flag: Flag,
  trophy: Trophy,
  graduation: GraduationCap,
};

export const audienceIcon = Users;
